import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Github, ExternalLink, Package, Server } from 'lucide-react';
import PageLayout from '../layout/PageLayout';
import ScrollingText from '../shared/ScrollingText';
import { projects } from '../../data/projects';

const HomePage: React.FC = () => {
  const featuredProject = projects.find(p => p.featured);

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background gradient effect */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            background: 'var(--gradient-bg)',
            filter: 'blur(100px)'
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Main tagline */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-theme font-bold mb-8">
              <span style={{ color: 'var(--color-text)' }}>We are </span>
              <span className="text-gradient">weebs</span>
              <span style={{ color: 'var(--color-text)' }}> who code.</span>
            </h1>

            {/* Scrolling subtitle */}
            <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-theme mb-8 h-16 flex items-center justify-center">
              <span style={{ color: 'var(--color-text-secondary)' }}>Everything we make here is </span>
              <span className="ml-3">
                <ScrollingText />
              </span>
            </div>

            {/* CTA Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <a
                href="https://github.com/theweebcoders"
                target="_blank"
                rel="noopener noreferrer"
                className="group px-8 py-4 rounded-lg font-semibold text-lg flex items-center space-x-2 transition-all hover:scale-105 shadow-md hover:shadow-glow"
                style={{
                  background: 'var(--gradient-primary)',
                  color: 'var(--color-bg)'
                }}
              >
                <Github size={24} />
                <span>View Our GitHub</span>
                <ExternalLink size={20} className="opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>

              <Link
                to="/projects"
                className="px-8 py-4 rounded-lg font-semibold text-lg border-2 transition-all hover:scale-105"
                style={{
                  borderColor: 'var(--color-primary)',
                  color: 'var(--color-primary)',
                  backgroundColor: 'var(--color-primary)' + '10'
                }}
              >
                See Our Projects
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Featured Project Section */}
      {featuredProject && (
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-theme font-bold mb-4 text-gradient">
                  Featured Project
                </h2>
                <p className="text-xl" style={{ color: 'var(--color-text-secondary)' }}>
                  Our flagship product (and currently our only product, but who's counting?)
                </p>
              </div>

              <div
                className="rounded-2xl p-8 md:p-12 shadow-lg backdrop-blur-sm"
                style={{
                  backgroundColor: 'var(--color-bg-secondary)',
                  borderColor: 'var(--color-border)',
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }}
              >
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  {/* Left: Project info */}
                  <div>
                    <h3 className="text-3xl md:text-4xl font-theme font-bold mb-2" style={{ color: 'var(--color-primary)' }}>
                      {featuredProject.name}
                    </h3>
                    <p className="text-xl font-semibold mb-4 italic" style={{ color: 'var(--color-text-secondary)' }}>
                      {featuredProject.tagline}
                    </p>

                    <div className="space-y-3 mb-6">
                      {featuredProject.longDescription.map((paragraph, i) => (
                        <p key={i} style={{ color: 'var(--color-text)' }}>
                          {paragraph}
                        </p>
                      ))}
                    </div>

                    {/* Technologies */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {featuredProject.technologies.map(tech => (
                        <span
                          key={tech}
                          className="px-3 py-1 rounded-full text-sm font-medium"
                          style={{
                            backgroundColor: 'var(--color-primary)' + '20',
                            color: 'var(--color-primary)'
                          }}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>

                    {/* Links */}
                    <div className="flex flex-wrap gap-3">
                      <a
                        href={featuredProject.githubLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all hover:scale-105"
                        style={{
                          backgroundColor: 'var(--color-primary)',
                          color: 'var(--color-bg)'
                        }}
                      >
                        <Github size={20} />
                        <span>GitHub</span>
                      </a>

                      {featuredProject.dockerLink && (
                        <a
                          href={featuredProject.dockerLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg font-medium border-2 transition-all hover:scale-105"
                          style={{
                            borderColor: 'var(--color-primary)',
                            color: 'var(--color-primary)',
                            backgroundColor: 'var(--color-primary)' + '10'
                          }}
                        >
                          <Server size={20} />
                          <span>Docker Hub</span>
                        </a>
                      )}

                      <Link
                        to={featuredProject.link}
                        className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg font-medium border-2 transition-all hover:scale-105"
                        style={{
                          borderColor: 'var(--color-primary)',
                          color: 'var(--color-primary)',
                          backgroundColor: 'var(--color-primary)' + '10'
                        }}
                      >
                        <Package size={20} />
                        <span>Learn More</span>
                      </Link>
                    </div>
                  </div>

                  {/* Right: Screenshot */}
                  <div className="relative">
                    <img
                      src="/images/commercial-breaker/page1.png"
                      alt="Commercial Breaker - Welcome to the Absolution"
                      className="w-full h-auto rounded-xl shadow-lg"
                      style={{
                        borderColor: 'var(--color-border)',
                        borderWidth: '2px',
                        borderStyle: 'solid'
                      }}
                    />
                    <Link
                      to="/commercial-breaker"
                      className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-30 transition-all rounded-xl group"
                    >
                      <span
                        className="opacity-0 group-hover:opacity-100 transition-opacity px-4 py-2 rounded-lg font-semibold"
                        style={{
                          background: 'var(--gradient-primary)',
                          color: 'var(--color-bg)'
                        }}
                      >
                        View All Screenshots →
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Quick Philosophy Teaser */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-theme font-bold mb-8 text-gradient">
              Our Philosophy
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { number: 1, text: 'Everything is public domain' },
                { number: 2, text: 'Always go full weeb' },
                { number: 3, text: 'Over-engineering is a feature' }
              ].map(item => (
                <motion.div
                  key={item.number}
                  className="p-6 rounded-xl"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)',
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  }}
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <div
                    className="text-4xl font-theme font-bold mb-2"
                    style={{ color: 'var(--color-primary)' }}
                  >
                    {item.number}
                  </div>
                  <p className="text-lg" style={{ color: 'var(--color-text)' }}>
                    {item.text}
                  </p>
                </motion.div>
              ))}
            </div>
            <Link
              to="/philosophy"
              className="inline-block mt-8 px-6 py-3 rounded-lg font-medium transition-all hover:scale-105"
              style={{
                backgroundColor: 'var(--color-primary)' + '20',
                color: 'var(--color-primary)'
              }}
            >
              Read the Full Manifesto →
            </Link>
          </motion.div>
        </div>
      </section>
    </PageLayout>
  );
};

export default HomePage;
